/**
 * Edit Yard Modal
 *
 * Modal dialog for editing existing storage yards.
 * Uses react-hook-form with Zod validation for robust form handling.
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useUpdateStorageYard } from '@/hooks/useStorageYards';
import { updateStorageYardSchema, type UpdateStorageYardDTO } from '@/schemas/storageYardSchema';
import { ApiError } from '@/services/api';
import type { StorageYard } from '@/services/storage-yard.service';

interface EditYardModalProps {
  open: boolean;
  onClose: () => void;
  yard: StorageYard | null;
}

export function EditYardModal({ open, onClose, yard }: EditYardModalProps) {
  const updateYard = useUpdateStorageYard();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<UpdateStorageYardDTO>({
    resolver: zodResolver(updateStorageYardSchema),
  });

  // Reset form when yard changes
  useEffect(() => {
    if (yard) {
      reset({
        name: yard.name,
        address: yard.address,
        region: yard.region || '',
        capacity: yard.capacity,
        notes: yard.notes || '',
        isDefault: yard.isDefault,
        isActive: yard.isActive,
      });
    }
  }, [yard, reset]);

  const onSubmit = async (data: UpdateStorageYardDTO) => {
    if (!yard) return;

    try {
      // Clean up optional fields
      const submitData: UpdateStorageYardDTO = {
        ...data,
        region: data.region?.trim() || undefined,
        notes: data.notes?.trim() || undefined,
        capacity: data.capacity || undefined,
      };

      await updateYard.mutateAsync({ id: yard.id, data: submitData });
      reset();
      onClose();
    } catch (error) {
      // Handle API validation errors with field-level details
      if (error instanceof ApiError && error.details && error.details.length > 0) {
        error.details.forEach((detail) => {
          setError(detail.field as keyof UpdateStorageYardDTO, {
            type: 'server',
            message: detail.message,
          });
        });
      }
      // Generic error toast handled by mutation
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!yard) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Storage Yard</DialogTitle>
          <DialogDescription>
            Update storage yard information. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Yard Name */}
          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-900">
              Yard Name <span className="text-red-600">*</span>
            </label>
            <input
              {...register('name')}
              id="name"
              type="text"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g., Main Yard, North Facility"
            />
            {errors.name && (
              <p className="text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          {/* Address */}
          <div className="space-y-2">
            <label htmlFor="address" className="block text-sm font-medium text-gray-900">
              Address <span className="text-red-600">*</span>
            </label>
            <textarea
              {...register('address')}
              id="address"
              rows={2}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="123 Main St, City, State 12345"
            />
            {errors.address && (
              <p className="text-sm text-red-600">{errors.address.message}</p>
            )}
          </div>

          {/* Region and Capacity Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Region */}
            <div className="space-y-2">
              <label htmlFor="region" className="block text-sm font-medium text-gray-900">
                Region <span className="text-gray-500 text-xs">(optional)</span>
              </label>
              <input
                {...register('region')}
                id="region"
                type="text"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Northern California"
              />
              {errors.region && (
                <p className="text-sm text-red-600">{errors.region.message}</p>
              )}
            </div>

            {/* Capacity */}
            <div className="space-y-2">
              <label htmlFor="capacity" className="block text-sm font-medium text-gray-900">
                Capacity <span className="text-gray-500 text-xs">(optional)</span>
              </label>
              <input
                {...register('capacity', {
                  setValueAs: (v) => v === '' ? undefined : parseInt(v, 10)
                })}
                id="capacity"
                type="number"
                min="0"
                step="1"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., 50"
              />
              {errors.capacity && (
                <p className="text-sm text-red-600">{errors.capacity.message}</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-900">
              Notes <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <textarea
              {...register('notes')}
              id="notes"
              rows={3}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Any additional information about this yard..."
            />
            {errors.notes && (
              <p className="text-sm text-red-600">{errors.notes.message}</p>
            )}
          </div>

          {/* Checkboxes */}
          <div className="space-y-3 pt-2 border-t border-gray-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                {...register('isDefault')}
                type="checkbox"
                className="w-4 h-4 mt-0.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">Set as default yard</span>
                <p className="text-xs text-gray-500 mt-0.5">
                  New containers will be assigned to this yard by default
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                {...register('isActive')}
                type="checkbox"
                className="w-4 h-4 mt-0.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">Active</span>
                <p className="text-xs text-gray-500 mt-0.5">
                  Only active yards can receive containers
                </p>
              </div>
            </label>
          </div>

          {/* Footer Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
